/* Native cart wallets, displayed on the PDP only after an explicit add action. */
(() => {
  if (customElements.get('calmaura-express')) return;
  class CalmauraExpress extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const {signal} = this.controller;
      this.form = this.closest('[data-ci-product]')?.querySelector('form[action*="/cart/add"]');
      if (!this.form) return;
      this.trigger = this.querySelector('button');
      this.error = this.querySelector('[role=alert]');
      this.dialog = this.querySelector('dialog');
      this.content = this.dialog.querySelector('[data-ci-express-content]');
      this.token = crypto.randomUUID();
      // Share this page's line identity with the native form. Switching from the
      // drawer to express payment reuses the selected pack instead of adding twice.
      this.lineIdentity = document.createElement('input');
      this.lineIdentity.type = 'hidden';
      this.lineIdentity.name = 'properties[_calmaura_express]';
      this.lineIdentity.value = this.token;
      this.form.append(this.lineIdentity);
      this.root = window.Shopify?.routes?.root || '/';
      document.body.append(this.dialog);
      this.hidden = false;
      try {
        if (window.ApplePaySession?.canMakePayments()) this.trigger.textContent = 'Choose Apple Pay or another wallet';
      } catch (_) { /* Native wallet controls determine actual eligibility. */ }
      this.label = this.trigger.textContent;
      this.trigger.addEventListener('click', () => this.open(), {signal});
      this.dialog.querySelector('[data-ci-express-close]').addEventListener('click', () => this.dialog.close(), {signal});
      this.dialog.addEventListener('close', () => this.trigger.focus({preventScroll:true}), {signal});
      // Once this panel has added the pack, the main CTA must not add it again.
      // Horizon delegates submits at document capture; intercept at window first.
      window.addEventListener('submit', event => {
        if (event.target !== this.form || !this.hasAdded) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.showCart();
      }, {capture:true, signal});
    }
    async request(path, options = {}) {
      const response = await fetch(this.root + path, {...options, headers: {Accept:'application/json', ...options.headers}});
      const result = await response.json();
      if (!response.ok || result.status) {
        const error = new Error(result.description || result.message || 'Unable to prepare your cart. Please try again.');
        error.status = response.status;
        throw error;
      }
      return result;
    }
    async ensurePack() {
      const data = new FormData(this.form);
      const variant = String(data.get('id') || '');
      const quantity = Number(data.get('quantity')) || 1;
      if (!variant || !Number.isInteger(quantity) || quantity < 1) throw new Error('Choose your pack first.');
      const cart = await this.request('cart.js');
      const existing = cart.items.find(item => item.properties?._calmaura_express === this.token);
      if (existing && String(existing.variant_id) !== variant) throw new Error('Your product selection changed. Please review your cart before continuing.');
      if (existing) {
        this.hasAdded = true;
        this.addUncertain = false;
        if (existing.quantity !== quantity) await this.request('cart/change.js', {method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({id:existing.key,quantity})});
      } else {
        if (this.addUncertain) throw new Error('The previous add could not be confirmed. Please review your cart before continuing.');
        // A private line identifier lets reopening update only this pack, preserving other items.
        data.delete('return_to'); data.delete('sections'); data.delete('sections_url');
        data.set('properties[_calmaura_express]', this.token);
        this.hasAdded = true; // A disconnected response may still have added the item; retry reads cart first.
        this.addUncertain = true;
        try {
          await this.request('cart/add.js', {method:'POST',body:data});
          this.addUncertain = false;
        } catch (error) {
          if (error.status >= 400 && error.status < 500) this.addUncertain = false;
          throw error;
        }
      }
    }
    setBusy(value) {
      this.busy = value;
      this.trigger.disabled = value;
      this.trigger.textContent = value ? 'Preparing your payment options…' : this.label;
      this.form.setAttribute('aria-busy', String(value));
      // Prevent pack changes while the selected quantity is being added.
      this.radios ||= [...this.closest('[data-ci-product]').querySelectorAll('calmaura-pack-picker input')];
      this.radios.forEach(input => { input.disabled = value; });
    }
    showError(error) {
      this.error.textContent = error instanceof TypeError ? 'Connection interrupted. Please try again to check your cart before continuing.' : error.message;
      this.error.hidden = false;
    }
    async open() {
      if (this.busy || this.form.getAttribute('aria-busy') === 'true') return;
      this.error.hidden = true;
      this.setBusy(true);
      try {
        await this.ensurePack();
        await this.refreshCart(false);
        const response = await fetch(this.root + 'cart?section_id=calmaura-express-checkout', {headers:{Accept:'text/html'}});
        if (!response.ok) throw new Error('Your pack is in your cart, but payment options could not load. Please try again.');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const cart = doc.querySelector('[data-ci-express-cart]');
        if (!cart || Number(cart.dataset.count) < 1) throw new Error('Please review your cart and try again.');
        this.content.replaceChildren(document.importNode(cart, true));
        this.dialog.showModal();
      } catch (error) { this.showError(error); }
      finally { this.setBusy(false); }
    }
    async refreshCart(openDrawer) {
      const ids = [...new Set([...document.querySelectorAll('cart-items-component')].map(el => el.dataset.sectionId).filter(Boolean))];
      const cart = await this.request('cart.js');
      const sections = ids.length ? await this.request('cart?sections=' + encodeURIComponent(ids.join(','))) : {};
      const {CartAddEvent} = await import('@theme/events');
      if (ids.length) await customElements.whenDefined('cart-items-component');
      if (document.querySelector('cart-drawer-component')) await customElements.whenDefined('cart-drawer-component');
      this.dispatchEvent(new CartAddEvent(cart, 'calmaura-express', {
        source: 'calmaura-express', itemCount: cart.item_count, sections, openDrawer,
      }));
      if (openDrawer && !document.querySelector('cart-drawer-component[auto-open]')) {
        window.location.assign(this.root + 'cart');
      }
    }
    async showCart() {
      if (this.busy) return;
      this.setBusy(true);
      this.error.hidden = true;
      try {
        await this.ensurePack();
        await this.refreshCart(true);
      } catch (error) { this.showError(error); }
      finally { this.setBusy(false); }
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.dialog?.remove();
      this.lineIdentity?.remove();
      this.controller = null;
    }
  }
  customElements.define('calmaura-express', CalmauraExpress);
})();
